import type { TFunction } from 'i18next';
import { z } from 'zod';

import type {
  AnimalRow,
  ProcedureCatalogOption,
} from '@/services/animal-management';

/** Legacy procedure tables: LOPU donors share the donor table. */
export type ProcedureKind = 'donor' | 'recipient' | 'calf';

const KIND_BY_TYPE: Record<AnimalRow['animalType'], ProcedureKind> = {
  Donor: 'donor',
  'LOPU Donor': 'donor',
  Recipient: 'recipient',
  Calf: 'calf',
};

// applicableTypes on procedure codes (AnimalTypes on the API).
const CATALOG_TYPE_BY_KIND: Record<ProcedureKind, number> = {
  donor: 1,
  recipient: 3,
  calf: 4,
};

export const procedureKindOf = (animalType: AnimalRow['animalType']) =>
  KIND_BY_TYPE[animalType];

/** The shared procedure kind of the selection, or null when it is empty or mixed. */
export function selectionProcedureKind(
  animals: Pick<AnimalRow, 'animalType'>[],
): ProcedureKind | null {
  const kinds = new Set(animals.map((a) => procedureKindOf(a.animalType)));
  return kinds.size === 1 ? [...kinds][0] : null;
}

export const catalogForKind = (
  options: ProcedureCatalogOption[],
  kind: ProcedureKind,
) =>
  options.filter(
    (o) =>
      o.animalTypeCode == null ||
      o.animalTypeCode === CATALOG_TYPE_BY_KIND[kind],
  );

// OvaDrive picklists: tog_time (donor/recip) and tog_timeofday (calf).
const AM = 100000000;
const PM = 100000001;
const FIRST_CALF_SLOT = 100000002;
const CALF_SLOT_MINUTES = 5;

export type TimeOption = { id: string; name: string };

const formatSlot = (minutes: number) => {
  const hours24 = Math.floor(minutes / 60);
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const mins = minutes % 60;
  return `${String(hours12).padStart(2, '0')}:${String(mins).padStart(2, '0')} ${hours24 < 12 ? 'AM' : 'PM'}`;
};

const AM_PM_OPTIONS: TimeOption[] = [
  { id: String(AM), name: 'AM' },
  { id: String(PM), name: 'PM' },
];

const CALF_TIME_OPTIONS: TimeOption[] = [
  ...AM_PM_OPTIONS,
  ...Array.from({ length: (24 * 60) / CALF_SLOT_MINUTES }, (_, i) => ({
    id: String(FIRST_CALF_SLOT + i),
    name: formatSlot(i * CALF_SLOT_MINUTES),
  })),
];

export const timeOptionsFor = (kind: ProcedureKind) =>
  kind === 'calf' ? CALF_TIME_OPTIONS : AM_PM_OPTIONS;

/** Which optional fields each procedure table stores. */
export const PROCEDURE_FIELDS: Record<
  ProcedureKind,
  {
    description: boolean;
    pregnant: boolean;
    owner: boolean;
    technician: boolean;
    diagnosis: boolean;
    temperature: boolean;
    wholeWeight: boolean;
  }
> = {
  donor: {
    description: true,
    pregnant: true,
    owner: true,
    technician: false,
    diagnosis: false,
    temperature: false,
    wholeWeight: false,
  },
  recipient: {
    description: false,
    pregnant: true,
    owner: false,
    technician: true,
    diagnosis: false,
    temperature: false,
    wholeWeight: true,
  },
  calf: {
    description: true,
    pregnant: false,
    owner: false,
    technician: false,
    diagnosis: true,
    temperature: true,
    wholeWeight: true,
  },
};

// Legacy column sizes.
export const PROCEDURE_DESCRIPTION_MAX: Record<ProcedureKind, number> = {
  donor: 125,
  recipient: 100,
  calf: 100,
};
export const PROCEDURE_UNIT_MAX = 5;

const optionalNumber = z.number().nullable();

export function buildAddProceduresSchema(kind: ProcedureKind, t: TFunction) {
  const required = t('common.required', 'Required');
  const descriptionMax = PROCEDURE_DESCRIPTION_MAX[kind];
  const item = z
    .object({
      procedureCatalogId: z.string().trim().min(1, required),
      description: z.string().max(
        descriptionMax,
        t('animalManagement.writes.tooLong', {
          defaultValue: 'Must be {max} characters or fewer',
          max: descriptionMax,
        }),
      ),
      enteredById: z.string().trim().min(1, required),
      date: z
        .date()
        .optional()
        .refine((value) => value instanceof Date, required),
      time: z.string(),
      drugId: z.string(),
      drugLabel: z.string(),
      dose: optionalNumber,
      unit: z.string().max(
        PROCEDURE_UNIT_MAX,
        t('animalManagement.writes.tooLong', {
          defaultValue: 'Must be {max} characters or fewer',
          max: PROCEDURE_UNIT_MAX,
        }),
      ),
      weight: optionalNumber,
      pregnant: z.enum(['Yes', 'No', '']),
      ownerId: z.string(),
      technicianId: z.string(),
      diagnosisId: z.string(),
      diagnosisLabel: z.string(),
      temperature: optionalNumber,
    })
    .superRefine((value, ctx) => {
      if (value.dose != null && value.dose < 0)
        ctx.addIssue({
          code: 'custom',
          path: ['dose'],
          message: t(
            'animalManagement.writes.notNegative',
            'Cannot be negative',
          ),
        });
      if (value.weight != null) {
        if (value.weight < 0)
          ctx.addIssue({
            code: 'custom',
            path: ['weight'],
            message: t(
              'animalManagement.writes.notNegative',
              'Cannot be negative',
            ),
          });
        else if (
          PROCEDURE_FIELDS[kind].wholeWeight &&
          !Number.isInteger(value.weight)
        )
          ctx.addIssue({
            code: 'custom',
            path: ['weight'],
            message: t(
              'animalManagement.writes.wholeNumber',
              'Enter a whole number',
            ),
          });
      }
      if (value.temperature != null && value.temperature <= 0)
        ctx.addIssue({
          code: 'custom',
          path: ['temperature'],
          message: t(
            'animalManagement.writes.greaterThanZero',
            'Must be greater than 0',
          ),
        });
    });
  return z.object({ procedures: z.array(item).min(1) });
}

export type AddProceduresFormValues = z.infer<
  ReturnType<typeof buildAddProceduresSchema>
>;
export type AddProcedureFormItem =
  AddProceduresFormValues['procedures'][number];

export const createEmptyProcedure = (
  enteredById = '',
): AddProcedureFormItem => ({
  procedureCatalogId: '',
  description: '',
  enteredById,
  date: undefined as unknown as Date,
  time: '',
  drugId: '',
  drugLabel: '',
  dose: null,
  unit: '',
  weight: null,
  pregnant: '',
  ownerId: '',
  technicianId: '',
  diagnosisId: '',
  diagnosisLabel: '',
  temperature: null,
});
